import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import React, { useEffect, useMemo, useState } from 'react';
import { fmtPersonTime } from '@/utils/personTime';
import useSWR from 'swr';
import { axiosInstance } from '@/api/api';
import {
  AreaChart,
  Area,
  Bar,
  BarChart,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceArea,
  ReferenceLine,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { ChevronDown, ChevronLeft, ChevronRight, Loader2, Maximize2 } from 'lucide-react';
import { InfoHint } from '@/components/report/MetricCard';

import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { listRacksByZone } from '@/api/racks';
import { listAreasByZone } from '@/api/areas';
import { origAspect as origBoxAspect, origGeomFromPolygon, type OrigGeom } from '@/components/report/day/OrigCropView';
import { Combobox } from '@/components/ui/combobox';
import { ImageLightbox } from '@/components/ui/image-lightbox';
import { useT } from '@/contexts/I18nContext';
import { useFrameTime } from '@/components/ui/hover-time';
import {
  DayPhotoStrip,
  buildRackStrip,
  type DayStripPhoto,
} from '@/components/report/day/DayPhotoStrip';
import { getLayoutDetail, getLayoutRacksSummary, getQueueDetail, whDomain, type LayoutDetailRack, type LayoutDetailResponse, type LayoutDetailShelfPoint, type LayoutRackSummary } from '@/api/analytics';
import { fmtDayTime, makeTimeAxis, parseBucketMs } from '@/components/report/day/timeAxis';
import { MarqueeZoom } from '@/components/report/MarqueeZoom';
import { queueBucketMs } from './AnalyticsQueueDetail';
import { Tooltip as UiTooltip } from '@/components/ui/tooltip';

// One colour per shelf line. Recharts will recycle past index 6, which is
// fine — racks rarely have more than 5 shelves in practice.
const SHELF_COLORS = [
  '#6366f1', // indigo
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#06b6d4', // cyan
];

// Sentinel rackId for the «Все» picker option (stacks a section per rack).
const ALL_RACKS = -1;

type ChartGranularity = 'hour' | 'day' | 'week' | '15min' | '5min' | 'raw';

// Position encoded in a shelf's name: digits first, then Russian ordinal
// stems, then height words (нижняя = on the floor = 1). Unknown → Infinity
// (falls through to the alphabetical tiebreak).
const SHELF_WORD_RANKS: Array<[RegExp, number]> = [
  [/нижн/i, 1],
  [/средн/i, 2],
  [/верхн/i, 3],
  [/перв/i, 1],
  [/втор/i, 2],
  [/трет/i, 3],
  [/четв[её]рт/i, 4],
  [/пят/i, 5],
  [/шест/i, 6],
  [/седьм/i, 7],
  [/восьм/i, 8],
  [/девят/i, 9],
  [/десят/i, 10],
];

function shelfNameRank(name: string | null | undefined): number {
  if (!name) return Infinity;
  const digits = name.match(/\d+/);
  if (digits) return parseInt(digits[0], 10);
  for (const [re, rank] of SHELF_WORD_RANKS) {
    if (re.test(name)) return rank;
  }
  return Infinity;
}

function formatTickDate(d: string, granularity: ChartGranularity): string {
  if (granularity === '15min' || granularity === '5min' || granularity === 'raw') {
    // tz-aware ISO from the BE → render in the browser's local time (HH:MM).
    const dt = new Date(d);
    if (!Number.isNaN(dt.getTime())) {
      return dt.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    }
    return d;
  }
  if (granularity === 'hour') {
    // Offset-less UTC hour from the BE → local «08.10 08:00» (#5811).
    const ms = parseBucketMs(d);
    if (Number.isNaN(ms)) return d;
    const dt = new Date(ms);
    const p2 = (n: number) => String(n).padStart(2, '0');
    return `${p2(dt.getDate())}.${p2(dt.getMonth() + 1)} ${p2(dt.getHours())}:${p2(dt.getMinutes())}`;
  }
  const parts = d.split('-');
  return `${parts[2]}.${parts[1]}`;
}

function fmtClockMs(ms: number): string {
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

// One-line tooltip for grid cells: «HH:MM · N%». Small enough to never spill
// out of a mini chart (which used to add page-level horizontal scroll).
function MiniShelfTooltip(props: {
  active?: boolean;
  payload?: Array<{ value?: number | string | null }>;
  label?: string | number;
  shelfCount: number;
}) {
  const { active, payload, label, shelfCount } = props;
  if (!active || !payload || !payload.length) return null;
  const total = payload.reduce((s, p) => s + (Number(p.value) || 0), 0);
  const rackPct = Math.round(total / Math.max(shelfCount, 1));
  return (
    <div className="whitespace-nowrap rounded border border-border bg-card px-1.5 py-0.5 text-[10px] text-foreground shadow-sm">
      {fmtClockMs(Number(label))} · {rackPct}%
    </div>
  );
}

// Tooltip with the rack's overall fill (Σ shelves ÷ shelf count) on top, then
// each shelf's occupancy — matching the ÷shelves Y axis.
function ShelfTooltip(props: {
  active?: boolean;
  payload?: Array<{ dataKey?: string | number; value?: number | string | null; name?: string; color?: string }>;
  label?: string | number;
  granularity: ChartGranularity;
  timeAxis: boolean;
  shelfCount: number;
  /** dataKey → rank; rows list in SHELF order (полка 1, 2, …), not by value. */
  shelfOrder: Record<string, number>;
}) {
  const { active, payload, label, granularity, timeAxis, shelfCount, shelfOrder } = props;
  if (!active || !payload || !payload.length) return null;
  const total = payload.reduce((s, p) => s + (Number(p.value) || 0), 0);
  const rackPct = Math.round(total / Math.max(shelfCount, 1));
  // Time axis: the tooltip ALWAYS names the day («пн 13.10, 14:30») — a bare
  // clock on a multi-day or zoomed chart was easy to misread (client via Alexey 13.10).
  const lbl = timeAxis ? fmtDayTime(Number(label)) : formatTickDate(String(label), granularity);
  const items = [...payload].sort(
    (a, b) => (shelfOrder[String(a.dataKey)] ?? 99) - (shelfOrder[String(b.dataKey)] ?? 99),
  );
  return (
    <div className="rounded-lg border border-border bg-card p-2 text-xs text-foreground shadow-md">
      <div className="mb-1 text-muted-foreground">{lbl}</div>
      <div className="mb-1.5 font-semibold">Заполнение стеллажа: {rackPct}%</div>
      <div className="space-y-0.5">
        {items.map((p) => (
          <div key={String(p.dataKey)} className="flex items-center gap-1.5">
            <span className="inline-block size-2 shrink-0 rounded-sm" style={{ background: p.color }} />
            <span className="min-w-0 flex-1 truncate">{p.name}</span>
            <span className="tabular-nums">{Math.round(Number(p.value) || 0)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ShelfTimeseriesChart({
  data,
  granularity,
  label,
  bare = false,
  height = 260,
  domain,
  onPointClick,
  showLegend = true,
  mini = false,
  highlight,
}: {
  data: LayoutDetailResponse['shelf_timeseries'];
  granularity: ChartGranularity;
  label: string;
  /** Skip the Card wrapper (for embedding inside another card, e.g. the report hero). */
  bare?: boolean;
  /** Pixel height, or '100%' to fill a flex parent (the stats page fill mode). */
  height?: number | `${number}%`;
  /** Working-hours [startMs, endMs] → pins a numeric time X axis (15-min mode). */
  domain?: [number, number];
  /** Click a chart point → caller opens the photo for that moment (ISO). */
  onPointClick?: (momentIso: string) => void;
  /** Hide the legend for compact per-rack mini charts. */
  showLegend?: boolean;
  /** Grid-cell mode: no axes, no legend, a fixed 0–100% Y scale (uniform
   *  across cells so cards compare apples-to-apples) and a one-line tooltip
   *  that stays inside the cell (no page overflow). */
  mini?: boolean;
  /** [startMs, endMs] to shade as a band (the report's problem window) —
   *  time-axis mode only. Clipped to the visible domain. */
  highlight?: [number, number];
}) {
  // Sort shelves by shelf_num_on_rack ascending. On many older racks the
  // number is degenerate (every shelf = 1), so tie-break by the position
  // encoded in the NAME — digits ("Полка 2 XM"), Russian ordinal words
  // ("Первая/Вторая/Третья…"), or height words ("Нижняя/Средняя/Верхняя").
  // A plain alphabetical fallback put «Вторая» before «Первая». First in
  // the array = bottom of the stack in Recharts (declaration order = stack
  // order), so shelf #1 lands on the floor, the way a real rack sits.
  const shelvesOrdered = useMemo(
    () =>
      [...data.shelves].sort(
        (a, b) =>
          a.shelf_num_on_rack - b.shelf_num_on_rack ||
          shelfNameRank(a.name) - shelfNameRank(b.name) ||
          (a.name || '').localeCompare(b.name || '', 'ru', { numeric: true }) ||
          a.id - b.id,
      ),
    [data.shelves],
  );

  // Legend-click isolation: click a shelf's legend row → only that shelf
  // stays on the chart; click it again → back to all shelves. Reset when the
  // rack (shelf set) changes.
  // Keyed on the shelf IDS, not the array: a refetch of the same rack (a new
  // period picked by dragging) must keep the isolated shelf.
  const [soloShelfId, setSoloShelfId] = useState<number | null>(null);
  const shelfIdsKey = data.shelves.map((s) => s.id).join(',');
  useEffect(() => {
    setSoloShelfId(null);
  }, [shelfIdsKey]);
  const soloActive = soloShelfId != null && shelvesOrdered.some((s) => s.id === soloShelfId);
  const visibleCount = soloActive ? 1 : shelvesOrdered.length;

  // Intraday granularities keep a REAL time axis, so multi-day ranges show
  // each day as its own hump with nights as gaps (see the break rows below)
  // instead of a category axis gluing 23:00 straight to 07:00.
  const timeAxis = granularity !== 'day' && granularity !== 'week';

  const stepMs = granularity === 'hour' ? 3600_000 : granularity === '15min' ? 15 * 60_000 : 5 * 60_000;
  const chartData = useMemo(() => {
    const rows = data.points.map((p: LayoutDetailShelfPoint) => {
      const row: Record<string, string | number | null> = { date: p.date };
      if (timeAxis) {
        // Точка — середина корзины, а не её начало: час 20:00–21:00 стоял на 20:00, и ступень
        // вечерней нормы (с 20:00) приходилась на край данных — её рисовало ночью (#1948).
        const ms = parseBucketMs(p.date);
        row.ts = Number.isNaN(ms) ? null : ms + stepMs / 2;
      }
      for (const shelf of shelvesOrdered) {
        // Treat missing buckets as 0 so the stacked area keeps a flat
        // baseline instead of dropping. Recharts skips the segment when
        // a stackId member is null, which leaves jagged gaps in the
        // stacked rendering.
        const v = p.values[shelf.id];
        row[`shelf_${shelf.id}`] = v == null ? 0 : v;
      }
      return row;
    });
    if (!timeAxis) return rows;
    // BREAK rows in capture gaps (nights): a row with no shelf values makes
    // the stacked areas stop instead of connecting across the closed hours.
    const out: typeof rows = [];
    let prevTs: number | null = null;
    for (const row of rows) {
      const ts = typeof row.ts === 'number' ? row.ts : null;
      if (prevTs != null && ts != null && ts - prevTs > stepMs * 3) {
        out.push({ date: '', ts: prevTs + stepMs });
      }
      out.push(row);
      if (ts != null) prevTs = ts;
    }
    return out;
  }, [data.points, shelvesOrdered, timeAxis, stepMs]);

  // X axis = the pinned domain, else the data extent, so an unpinned chart
  // spanning several days still gets dated ticks.
  const xAxisDom: [number, number] | undefined = (() => {
    if (!timeAxis) return undefined;
    if (domain) return domain;
    const ts = chartData.map((r) => (typeof r.ts === 'number' ? r.ts : NaN)).filter((v) => Number.isFinite(v));
    return ts.length ? [Math.min(...ts), Math.max(...ts)] : undefined;
  })();
  const xAxis = makeTimeAxis(xAxisDom, mini ? 3 : 8);

  if (!data.shelves.length || !data.points.length) {
    // Grid cells (week «Выкладка за неделю») render a blank tile, NOT a «нет
    // данных» label: the grid prunes all-empty rows/columns right after each
    // cell reports, so the text would only flash for a frame during load
    // (Alexey 2026-07-21). A subtle box keeps the layout until it's pruned.
    if (mini) {
      return <div style={{ height }} className="rounded bg-muted/30" />;
    }
    const empty = (
      <div className="flex h-44 items-center justify-center text-sm text-muted-foreground">
        {label} — нет данных
      </div>
    );
    return bare ? empty : <Card><CardContent>{empty}</CardContent></Card>;
  }

  // Y-axis ceiling: each shelf contributes up to 100%, so 5 shelves stack
  // up to 500%. Compute the actual peak from data + a 10% headroom so the
  // top band breathes. With a solo shelf isolated, only it contributes.
  // Mini cells instead pin Y to the FULL 0–100% band so every card in a grid
  // shares one scale.
  const stackedShelves = soloActive
    ? shelvesOrdered.filter((s) => s.id === soloShelfId)
    : shelvesOrdered;
  // FIXED full scale (shelves x 100%), same as the mini charts always used.
  // It used to be the observed max stacked sum, padded x1.05 — which made the
  // percentage ticks land at arbitrary heights: on zone 39 the gap 50->100%
  // rendered the same size as 30->50%, and the point sitting level with the
  // "100%" label was actually 67% (Alexey 2026-08-11). A percentage axis has to
  // be absolute, otherwise every rack is drawn to a different, invisible scale
  // and two racks cannot be compared by eye.
  const yMax = stackedShelves.length * 100;
  // Плановая загруженность = нижняя граница нормы (Алексей 21.10 #1667): высота стопки, когда
  // каждая полка ровно на своём пороге (empty_shelf_score, та же шкала, что значения полок).
  // Полка с порогом 0 сознательно не анализируется — в норму не входит. Нет порогов — нет линии.
  const normY = stackedShelves.reduce((acc, s) => acc + (s.empty_shelf_score && s.empty_shelf_score > 0 ? s.empty_shelf_score * 100 : 0), 0);
  // Вечером норма ниже — по тому же расписанию, что алерты и отчёт дня (Алексей 21.10 #1669):
  // на временной оси линия ступенькой — днём норма, со старта ступени — норма × множитель.
  // На днях/неделях (точка = сутки) ступеньку не нарисовать — там линия дневной нормы.
  type NormSeg = { a: number; b: number; y: number; m: number };
  const normSegs: NormSeg[] = [];
  // Отрезки работы — те же «горбы», что у данных (между ними строки-разрывы ночью): норма
  // рисуется только внутри них, между днями линия рвётся (Алексей 22.10, #1944).
  const spans: [number, number][] = [];
  if (timeAxis) {
    let cur: [number, number] | null = null;
    for (const r of chartData) {
      const ts = typeof r.ts === 'number' ? r.ts : null;
      if (ts == null) continue;
      if (r.date === '') { if (cur) spans.push(cur); cur = null; continue; }
      // Отрезок — от начала первой корзины дня до конца последней.
      if (!cur) cur = [ts - stepMs / 2, ts + stepMs / 2]; else cur[1] = ts + stepMs / 2;
    }
    if (cur) spans.push(cur);
  }
  if (timeAxis && normY > 0) {
    const tsList = chartData.map((r) => (typeof r.ts === 'number' ? r.ts : NaN)).filter((v) => Number.isFinite(v));
    const x0 = domain?.[0] ?? (tsList.length ? Math.min(...tsList) : NaN);
    const x1 = domain?.[1] ?? (tsList.length ? Math.max(...tsList) : NaN);
    const steps = (data.threshold_steps ?? [])
      .map((st) => ({ ms: Date.parse(st.at), m: st.multiplier }))
      .filter((st) => Number.isFinite(st.ms))
      .sort((p, q) => p.ms - q.ms);
    if (Number.isFinite(x0) && Number.isFinite(x1)) {
      const bounds = [{ ms: -Infinity, m: 1 }, ...steps];
      for (let i = 0; i < bounds.length; i++) {
        const a = Math.max(bounds[i].ms, x0);
        const b = Math.min(i + 1 < bounds.length ? bounds[i + 1].ms : Infinity, x1);
        if (b > a) {
          const last = normSegs[normSegs.length - 1];
          if (last && last.m === bounds[i].m && last.b === a) last.b = b;
          else normSegs.push({ a, b, y: normY * bounds[i].m, m: bounds[i].m });
        }
      }
    }
  }
  // Без расписания вечера — одна ступень ×1 на весь период; затем всё режем по отрезкам работы.
  if (timeAxis && spans.length && normSegs.length) {
    const clipped: NormSeg[] = [];
    for (const [sa, sb] of spans) {
      for (const seg of normSegs) {
        const a = Math.max(seg.a, sa);
        const b = Math.min(seg.b, sb);
        if (b > a) clipped.push({ ...seg, a, b });
      }
    }
    normSegs.splice(0, normSegs.length, ...clipped);
  }
  const pctOf = (y: number) => Math.round(y / Math.max(visibleCount, 1));

  const inner = (
    <>
      {label && <div className="text-xs text-muted-foreground mb-2">{label}</div>}
      <ResponsiveContainer width="100%" height={height}>
          <AreaChart
            data={chartData}
            margin={mini ? { top: 0, right: 0, bottom: 0, left: 0 } : { top: 4, right: 8, bottom: 0, left: 0 }}
            // No focus ring / keyboard layer — clicking the chart (or its
            // dots) drew a blue outline around them.
            accessibilityLayer={false}
            onClick={
              onPointClick
                ? (state: { activeLabel?: string | number | null; activeIndex?: number | string | null }) => {
                    // recharts v3 chart-onClick exposes activeIndex / activeLabel
                    // (no activePayload). Resolve to the clicked point's ISO via
                    // the data row (most reliable), falling back to activeLabel.
                    const idx =
                      state?.activeIndex != null ? Number(state.activeIndex) : NaN;
                    const row =
                      Number.isFinite(idx) && idx >= 0 ? chartData[idx] : undefined;
                    let iso = row?.date != null ? String(row.date) : undefined;
                    if (!iso && state?.activeLabel != null) {
                      iso = timeAxis
                        ? new Date(Number(state.activeLabel)).toISOString()
                        : String(state.activeLabel);
                    }
                    if (iso) onPointClick(iso);
                  }
                : undefined
            }
            style={onPointClick ? { cursor: 'pointer' } : undefined}
          >
            {!mini && (
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" strokeOpacity={0.4} />
            )}
            {timeAxis ? (
              <XAxis
                dataKey="ts"
                type="number"
                scale="time"
                domain={domain ?? ['dataMin', 'dataMax']}
                // Shared time axis (timeAxis.ts makeTimeAxis): the FIRST value
                // (working-day open / zoom start) is always labelled; a domain
                // across midnight → «пн 20.10» at midnights, «20.10 14:00» for
                // a day's first tick; one day — the clock only (#2646). Without
                // a pinned domain (ObservationCard) it is the data extent.
                ticks={xAxis.ticks}
                tickFormatter={xAxis.format}
                tick={{ fontSize: mini ? 9 : 11, fill: 'var(--color-muted-foreground)' }}
                axisLine={mini ? { stroke: 'var(--color-border)' } : false}
                tickLine={false}
                minTickGap={32}
                height={mini ? 14 : undefined}
                // Honour a tighter [start,end] domain (e.g. the fullscreen
                // marquee-zoom) instead of expanding back to the data extent.
                allowDataOverflow
              />
            ) : (
              <XAxis
                dataKey="date"
                tickFormatter={(d) => formatTickDate(String(d), granularity)}
                tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
                hide={mini}
              />
            )}
            <YAxis
              domain={[0, yMax]}
              // Round 0/25/50/75/100% ticks (rack-level) — auto ticks labeled
              // the padded ceiling ("105%"); headroom stays, unlabeled. Ticks
              // beyond the domain are dropped by recharts.
              ticks={[0, 1, 2, 3, 4].map((i) => i * 25 * Math.max(visibleCount, 1))}
              tick={mini ? false : { fontSize: 11, fill: 'var(--color-muted-foreground)' }}
              axisLine={mini ? { stroke: 'var(--color-border)' } : false}
              tickLine={false}
              width={mini ? 4 : 44}
              // Stacked sum is up to shelves×100%; show the rack-level value
              // (÷ visible shelf count) so the axis reads 0–100%, not 400%.
              tickFormatter={(v) => `${Math.round(Number(v) / Math.max(visibleCount, 1))}%`}
            />
            <Tooltip
            isAnimationActive={false}
              // Mini cells: a one-liner clamped inside the cell (no page
              // overflow, side-offset from the cursor).
              allowEscapeViewBox={{ x: false, y: false }}
              offset={mini ? 16 : 10}
              content={
                mini ? (
                  <MiniShelfTooltip shelfCount={visibleCount} />
                ) : (
                  <ShelfTooltip
                    granularity={granularity}
                    timeAxis={timeAxis}
                    shelfCount={visibleCount}
                    shelfOrder={Object.fromEntries(
                      shelvesOrdered.map((s, i) => [`shelf_${s.id}`, i]),
                    )}
                  />
                )
              }
            />
            {showLegend && (
            <Legend
              wrapperStyle={{ fontSize: 11, paddingTop: 4, cursor: 'pointer' }}
              iconType="square"
              // recharts v3 SILENTLY sorts legend entries by their display
              // name (itemSorter defaults to 'value') — that's what kept
              // shuffling «Первая/Вторая/Третья полка» no matter how the
              // series were ordered. Sort by the shelf's rack position.
              itemSorter={(item: { dataKey?: unknown }) => {
                const idx = shelvesOrdered.findIndex((s) => `shelf_${s.id}` === String(item.dataKey));
                return idx < 0 ? 99 : idx;
              }}
              onClick={(entry: { dataKey?: unknown; payload?: { dataKey?: unknown } }) => {
                const dk = String(entry?.dataKey ?? entry?.payload?.dataKey ?? '');
                const id = Number(dk.replace('shelf_', ''));
                if (!Number.isFinite(id)) return;
                setSoloShelfId((cur) => (cur === id ? null : id));
              }}
              formatter={(value: string, entry: { dataKey?: unknown }) => {
                const dk = String(entry?.dataKey ?? '');
                const dimmed = soloActive && dk !== `shelf_${soloShelfId}`;
                return (
                  <span style={dimmed ? { opacity: 0.4 } : undefined}>{value}</span>
                );
              }}
            />
            )}
            {timeAxis && highlight && highlight[1] > highlight[0] && (
              <ReferenceArea
                x1={highlight[0]}
                x2={highlight[1]}
                ifOverflow="hidden"
                fill="var(--color-primary)"
                fillOpacity={0.12}
                stroke="var(--color-primary)"
                strokeOpacity={0.35}
                strokeDasharray="3 3"
              />
            )}
            {shelvesOrdered.map((shelf, i) => (
              <Area
                key={shelf.id}
                type="monotone"
                dataKey={`shelf_${shelf.id}`}
                name={shelf.name || `Полка ${shelf.shelf_num_on_rack}`}
                stackId="shelves"
                // Без линий между полками — только заливка (Алексей 30.10 #3394).
                stroke="none"
                fill={SHELF_COLORS[i % SHELF_COLORS.length]}
                fillOpacity={0.6}
                dot={false}
                isAnimationActive={false}
                hide={soloActive && shelf.id !== soloShelfId}
              />
            ))}
            {!mini && normSegs.length > 0 && normSegs.flatMap((seg, i) => {
              const prev = normSegs[i - 1];
              const firstOfKind = normSegs.findIndex((q) => q.m === seg.m) === i;
              // ×0 — подписи нет, линия просто уходит вниз (Алексей 21.10 #1671).
              const text = seg.m === 1 ? `норма ${pctOf(seg.y)}%` : seg.m === 0 ? '' : `вечером норма ${pctOf(seg.y)}%`;
              const out = [
                <ReferenceLine
                  key={`n${i}`}
                  segment={[{ x: seg.a, y: seg.y }, { x: seg.b, y: seg.y }]}
                  ifOverflow="hidden"
                  stroke="var(--color-foreground)"
                  strokeOpacity={0.7}
                  strokeWidth={1.5}
                  strokeDasharray="6 4"
                  label={firstOfKind && text ? {
                    value: text,
                    position: seg.m === 1 ? 'insideTopRight' : 'insideTopLeft',
                    fontSize: 10,
                    fill: 'var(--color-foreground)',
                    fillOpacity: 0.75,
                  } : undefined}
                />,
              ];
              if (prev && prev.b === seg.a && prev.y !== seg.y) {
                out.push(
                  <ReferenceLine
                    key={`v${i}`}
                    segment={[{ x: seg.a, y: prev.y }, { x: seg.a, y: seg.y }]}
                    ifOverflow="hidden"
                    stroke="var(--color-foreground)"
                    strokeOpacity={0.5}
                    strokeWidth={1}
                    strokeDasharray="2 3"
                  />,
                );
              }
              return out;
            })}
            {!mini && normY > 0 && normSegs.length === 0 && (
              <ReferenceLine
                y={normY}
                ifOverflow="extendDomain"
                stroke="var(--color-foreground)"
                strokeOpacity={0.7}
                strokeWidth={1.5}
                strokeDasharray="6 4"
                label={{
                  value: `норма ${Math.round(normY / Math.max(visibleCount, 1))}%`,
                  position: 'insideTopRight',
                  fontSize: 10,
                  fill: 'var(--color-foreground)',
                  fillOpacity: 0.75,
                }}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
    </>
  );

  if (bare) return inner;
  return (
    <Card>
      <CardContent className="pt-3 pb-2 px-3">{inner}</CardContent>
    </Card>
  );
}

// ── Per-rack full-width section: photos above, shelf chart below, zoomable ────

// Wide FIXED slot (≈3:2 at the 120px height): object-cover fills it, wide rack
// crops read well, and ~5-6 still fit across the card (Alexey 2026-07-22, 808c).
const RACK_STATS_SLOT_W = 180;
// FIXED heights for the stats layout card — both the per-lane photo height and
// the chart height are constants, so the card is sized correctly on the FIRST
// paint and never re-measures: no measure→setState→re-measure loop, so no
// up/down oscillation on load / rack-switch / refocus (Alexey 2026-07-21:
// «пляшет … а не один раз»). The card is content-sized (like the queue card,
// whose balance Alexey is happy with) — photos read at a comfortable size and
// the chart is proportionate, not a screen-filling giant over tiny photos.
// Photos are sized by a COUNT target, not size-first (Alexey 2026-07-21: «таргет
// по количеству фото … например >5»): a smaller per-lane height means the
// aspect-sized slots are narrower, so more crops fit across the width. 120px ×
// 2 lanes keeps them readable while showing well over five.
const RACK_STATS_IMG_H = 120;
// «В оригинальных пропорциях» (Алексей 20.10 AI298 → 21.10 #1671): фото стеллажа так, как он
// снят камерой — описывающий прямоугольник полигона: кусок исходного кадра, если сохранился,
// иначе выровненный кроп, натянутый обратно на полигон, с чёрным вокруг (OrigCropView).
// Пропорция — по размеру кадра стеллажа (initial_width/height), без него 16:9. Запоминается.
const ORIG_ASPECT_KEY = 'layout.photos.origAspect';
const FRAME_ASPECT = 16 / 9;
// 260 -> 215: the period-summary cards below pushed the single-rack view past the
// tab's locked height (Alexey 2026-08-11 «карточки выходят за заблокированный
// скролл»). Trimming the chart buys the cards their room on desktop; the wrapper
// below also scrolls, so nothing is ever cut off on short or mobile screens.
const RACK_STATS_CHART_H = 215;

// «Поток» под графиком выкладки (Alexey 13.10 #5810): столбцы очереди на той же
// оси времени, чтобы провал выкладки читался рядом с наплывом гостей.
// Сворачивается; состояние помнит браузер.
const FLOW_COLLAPSED_KEY = 'layout.flowStrip.collapsed';
function QueueFlowStrip({ zoneId, from, to, domain }: { zoneId: string; from: string; to: string; domain: [number, number] }) {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(FLOW_COLLAPSED_KEY) === '1'; } catch { return false; }
  });
  const toggle = () => setCollapsed((c) => {
    try { localStorage.setItem(FLOW_COLLAPSED_KEY, c ? '0' : '1'); } catch { /* приватное окно */ }
    return !c;
  });
  const span = domain[1] - domain[0];
  const bucket = span <= 2 * 864e5 ? 5 * 60_000 : queueBucketMs(from, to);
  const { data } = useSWR(
    zoneId && !collapsed ? ['layout:flow', zoneId, from, to, bucket, domain[0], domain[1]] : null,
    () => getQueueDetail(zoneId, from, to, bucket, {
      photoMode: 'even', photoCount: 1,
      fromTs: new Date(domain[0]).toISOString(), toTs: new Date(domain[1]).toISOString(),
    }).then((r) => r.data),
    { keepPreviousData: true, revalidateOnFocus: false },
  );
  if (data && !data.configured) return null;
  const rows = (data?.overlay ?? [])
    .map((p) => ({ ts: new Date(p.time).getTime(), queue: p.queue }))
    .filter((r) => r.ts >= domain[0] && r.ts <= domain[1]);
  return (
    <div>
      <button type="button" onClick={toggle}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        {collapsed ? <ChevronRight className="size-3.5" /> : <ChevronDown className="size-3.5" />}
        Поток — люди в очереди
      </button>
      {!collapsed && (
        <ResponsiveContainer width="100%" height={70}>
          <BarChart data={rows} margin={{ top: 4, right: 8, bottom: 0, left: 0 }} accessibilityLayer={false} barCategoryGap={0}>
            <XAxis dataKey="ts" type="number" scale="time" domain={domain} hide allowDataOverflow />
            <YAxis width={44} tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }} axisLine={false}
              tickLine={false} allowDecimals={false} tickCount={3} />
            <Tooltip isAnimationActive={false} cursor={{ fill: 'var(--color-muted)', opacity: 0.4 }}
              content={({ active, payload, label }) => active && payload?.length ? (
                <div className="whitespace-nowrap rounded border border-border bg-card px-1.5 py-0.5 text-[11px] text-foreground shadow-sm">
                  {fmtDayTime(Number(label))} · в очереди {Math.round(Number(payload[0].value) || 0)}
                </div>
              ) : null} />
            <Bar dataKey="queue" fill="var(--color-primary)" fillOpacity={0.55} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// «Продажи» под «Потоком» (Алексей 29.10 #3342, эксперимент): штуки позиций этого стеллажа по чекам на
// той же оси времени, площадями по позициям (топ + «Другое»). По умолчанию свёрнуто; состояние помнит браузер.
const SALES_OPEN_KEY = 'layout.salesStrip.open';
const SALES_COLORS = [...SHELF_COLORS, '#ec4899', '#14b8a6'];
type RackSalesDto = { configured: boolean; types?: string[]; offset_s?: number; bucket?: number;
  items?: { name: string; qty: number; rub?: number }[]; total_qty?: number; total_rub?: number;
  points?: ({ t: string; gap?: boolean; rub?: Record<string, number> } & Record<string, unknown>)[] };
const SALES_UNIT_KEY = 'layout.salesStrip.unit';
function RackSalesStrip({ zoneId, rackId, domain }: { zoneId: string; rackId: number; domain: [number, number] }) {
  const [open, setOpen] = useState<boolean>(() => {
    try { return localStorage.getItem(SALES_OPEN_KEY) === '1'; } catch { return false; }
  });
  const toggle = () => setOpen((o) => {
    try { localStorage.setItem(SALES_OPEN_KEY, o ? '0' : '1'); } catch { /* приватное окно */ }
    return !o;
  });
  // Интерактив как у полок (#3392): наведение подсвечивает позицию, клик оставляет только её.
  const [hover, setHover] = useState<string | null>(null);
  const [solo, setSolo] = useState<string | null>(null);
  const pick = (name: string) => setSolo((cur) => (cur === name ? null : name));
  // Шт / ₽ на графике (#3395); выбор помнит браузер.
  const [unit, setUnitState] = useState<'qty' | 'rub'>(() => {
    try { return localStorage.getItem(SALES_UNIT_KEY) === 'rub' ? 'rub' : 'qty'; } catch { return 'qty'; }
  });
  const setUnit = (u: 'qty' | 'rub') => {
    setUnitState(u);
    try { localStorage.setItem(SALES_UNIT_KEY, u); } catch { /* приватное окно */ }
  };
  const fmt = (v: number) => `${Math.round(v).toLocaleString('ru-RU')}${unit === 'rub' ? ' ₽' : ''}`;
  const span = domain[1] - domain[0];
  // Только внутри дня (#3389): длинный период — тот же часовой шаг, по дням график рвётся.
  const bucket = span <= 2 * 864e5 ? 15 : span <= 8 * 864e5 ? 30 : 60;
  const { data, error, isLoading } = useSWR(
    zoneId && rackId > 0 && open ? ['layout:rack-sales', zoneId, rackId, domain[0], domain[1], bucket] : null,
    () => axiosInstance.get<RackSalesDto>(`/ops_tasks/receipts/${zoneId}/rack-sales/`, { params: {
      rack: rackId, bucket, from_ts: new Date(domain[0]).toISOString(), to_ts: new Date(domain[1]).toISOString(),
    } }).then((r) => r.data),
    { keepPreviousData: true, revalidateOnFocus: false },
  );
  if (data && !data.configured) return null;
  const items = data?.items ?? [];
  const rows = (data?.points ?? []).map((p) => {
    const row: Record<string, number | null> = { ts: new Date(p.t).getTime() };
    for (const it of items) row[it.name] = p.gap ? null : Number((unit === 'rub' ? p.rub?.[it.name] : p[it.name]) ?? 0);
    return row;
  });
  const totalQty = data?.total_qty ?? items.reduce((a, b) => a + b.qty, 0);
  const totalRub = data?.total_rub ?? items.reduce((a, b) => a + (b.rub ?? 0), 0);
  const color = (name: string, i: number) => (name === 'Другое' ? SALES_COLORS[SALES_COLORS.length - 1] : SALES_COLORS[i % SALES_COLORS.length]);
  const per = bucket >= 60 ? `${bucket / 60} ч` : `${bucket} мин`;
  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
        <button type="button" onClick={toggle} className="flex items-center gap-1 hover:text-foreground">
          {open ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
          Продажи{data?.types?.length ? ` — ${data.types.join(', ').toLowerCase()}` : ''}, {unit === 'rub' ? '₽' : 'шт'} за {per}
        </button>
        {open && data && items.length > 0 && (
          <span className="tabular-nums">
            · всего за период: <span className="text-foreground">{Math.round(totalQty).toLocaleString('ru-RU')} шт, {Math.round(totalRub).toLocaleString('ru-RU')} ₽</span>
          </span>
        )}
        {open && isLoading && <Loader2 className="size-3 animate-spin" />}
        {open && (
          <span className="ml-auto inline-flex overflow-hidden rounded border border-border">
            {(['qty', 'rub'] as const).map((u) => (
              <button key={u} type="button" onClick={() => setUnit(u)}
                className={cn('px-1.5 py-0.5', unit === u ? 'bg-muted text-foreground' : 'hover:text-foreground')}>
                {u === 'rub' ? '₽' : 'шт'}
              </button>
            ))}
          </span>
        )}
      </div>
      {open && (error || (data && !items.length)) && (
        <div className="ml-11 py-2 text-xs text-muted-foreground">
          {error ? 'Не удалось загрузить продажи' : 'За этот период продаж позиций этого стеллажа нет'}
        </div>
      )}
      {open && !error && (!data || items.length > 0) && (
        <>
          <ResponsiveContainer width="100%" height={90}>
            <AreaChart data={rows} margin={{ top: 4, right: 8, bottom: 0, left: 0 }} accessibilityLayer={false}
              onMouseLeave={() => setHover(null)}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" strokeOpacity={0.4} vertical={false} />
              <XAxis dataKey="ts" type="number" scale="time" domain={domain} hide allowDataOverflow />
              <YAxis width={44} tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false}
                tickLine={false} allowDecimals={false} tickCount={3} />
              <Tooltip isAnimationActive={false} cursor={{ stroke: 'var(--color-border)' }}
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const shown = payload.filter((x) => Number(x.value) > 0).reverse();
                  return (
                    <div className="rounded border border-border bg-card px-1.5 py-1 text-[11px] text-foreground shadow-sm">
                      <div className="text-muted-foreground">{fmtDayTime(Number(label))}</div>
                      {shown.length ? shown.map((x) => (
                        <div key={String(x.dataKey)} className="flex items-center gap-1 whitespace-nowrap">
                          <span className="inline-block size-2 rounded-sm" style={{ background: color(String(x.dataKey), items.findIndex((it) => it.name === x.dataKey)) }} />
                          {String(x.dataKey)}: <span className="tabular-nums">{fmt(Number(x.value))}</span>
                        </div>
                      )) : <div>продаж нет</div>}
                    </div>
                  );
                }} />
              {items.map((it, i) => (
                <Area key={it.name} type="monotone" dataKey={it.name} stackId="sales" stroke="none"
                  fill={color(it.name, i)} fillOpacity={hover && hover !== it.name ? 0.2 : 0.7}
                  hide={solo != null && solo !== it.name} activeDot={false} dot={false} isAnimationActive={false}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setHover(it.name)} onClick={() => pick(it.name)} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
          {items.length > 0 && (
            <div className="ml-11 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
              {items.map((it, i) => (
                <button key={it.name} type="button" onClick={() => pick(it.name)}
                  onMouseEnter={() => setHover(it.name)} onMouseLeave={() => setHover(null)}
                  className={cn('flex items-center gap-1 hover:text-foreground',
                    solo != null && solo !== it.name && 'opacity-40', solo === it.name && 'text-foreground')}>
                  <span className="inline-block size-2 rounded-sm" style={{ background: color(it.name, i) }} />
                  {it.name} <span className="tabular-nums">{fmt(unit === 'rub' ? it.rub ?? 0 : it.qty)}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Minutes covered by one bucket of the given granularity. */
const BUCKET_MINUTES: Record<string, number> = { '5min': 5, '15min': 15, hour: 60, day: 1440 };

/** Zoomed window → finer buckets, so a marquee narrower than 15 min still shows
 *  a curve instead of one or two points (Alexey 24.09: «нельзя приблизить ближе
 *  чем 15 минут»). ≤2 h — every frame; ≤8 h — 5-min buckets. */
function zoomGranularity(zoom: [number, number]): '15min' | '5min' | 'raw' {
  const span = zoom[1] - zoom[0];
  return span <= 2 * 3600_000 ? 'raw' : span <= 8 * 3600_000 ? '5min' : '15min';
}

/** Fill-level line the «часов ниже X%» card counts against. Default 50%, and the
 *  user can step it 10..90 with the chevrons next to the number (Alexey
 *  2026-08-11: «давай лучше фикс, 50%, и рядом сделаем незаметную стрелку чтобы
 *  можно было выбрать 10, 20, 30 и т.д. с шагом в 10»).
 *
 *  Earlier versions derived the line from each shelf's own empty_shelf_score.
 *  Dropped on his call: the heading then had to say «ниже половины порога» for a
 *  rack whose shelves disagree, and a user cannot act on a number they can't see.
 *  How we compute it is our problem; what they read must be a concrete percent. */
const EMPTY_PCT_DEFAULT = 50;
const EMPTY_PCT_MIN = 10;
const EMPTY_PCT_MAX = 90;
const EMPTY_PCT_STEP = 10;

/** "2 ч 15 мин" / "45 мин" — minutes are what a manager can act on; a raw sample
 *  count means nothing to them (Alexey 2026-08-11). */
function fmtDuration(minutes: number): string {
  const m = Math.round(minutes);
  if (m < 60) return `${m} мин`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest === 0 ? `${h} ч` : `${h} ч ${rest} мин`;
}

/** Часы с одним знаком: «3.9 ч», «4 ч» — так же, как в отчёте дня. */
function fmtHours(minutes: number): string {
  return `${Number((minutes / 60).toFixed(1))} ч`;
}

/** «Ниже нормы» на чипе: за один день — часы, за несколько — часы в день (Алексей 22.10, #1799). */
function fmtBelow(minutes: number, periodDays: number, openDays: number): string {
  return periodDays > 1 ? `${fmtHours(minutes / openDays)} в день ниже нормы` : `${fmtHours(minutes)} ниже нормы`;
}

/** Period summary under the выкладка chart — two separate cards, matching how
 *  «Очередь» lays its derived charts out in their own row.
 *
 *  Every number here is TIME, derived from the same shelf_timeseries the chart
 *  above draws: one sample per time bucket (the rack's mean fill across its
 *  shelves), each worth BUCKET_MINUTES. Using the rack mean for the headline,
 *  the distribution AND the below-X hours keeps the three consistent — mixing a
 *  per-shelf rule into one of them would make the cards contradict each other.
 *
 *  At 'day' granularity (periods past ~5 weeks) a bucket is a whole day, so a
 *  daily average says nothing about hours spent under the line — the hours card
 *  says so instead of fabricating a number. */
function RackPeriodSummary({
  ts,
  granularity,
  from,
  to,
}: {
  ts: LayoutDetailResponse['shelf_timeseries'];
  granularity: '15min' | '5min' | 'raw' | 'hour' | 'day';
  from: string;
  to: string;
}) {
  const [emptyPct, setEmptyPct] = useState(EMPTY_PCT_DEFAULT);
  // Width of the distribution band — to tell when the average's label would
  // run into the 0% / 100% corner labels (#435: at 96% «96%» sat on «100%»).
  const [bandEl, setBandEl] = useState<HTMLDivElement | null>(null);
  const [bandW, setBandW] = useState(0);
  useEffect(() => {
    if (!bandEl) return;
    const measure = () => setBandW(bandEl.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(bandEl);
    return () => ro.disconnect();
  }, [bandEl]);

  const stats = useMemo(() => {
    // 'raw' has no fixed bucket: each frame is worth the mean frame interval.
    const rawTs = granularity === 'raw' ? ts.points.map((p) => new Date(p.date).getTime()) : [];
    const bucketMin =
      granularity === 'raw'
        ? rawTs.length > 1 ? (rawTs[rawTs.length - 1] - rawTs[0]) / (rawTs.length - 1) / 60_000 : 0
        : BUCKET_MINUTES[granularity] ?? 15;
    const perBucket: number[] = [];
    for (const p of ts.points) {
      let sum = 0;
      let n = 0;
      for (const sh of ts.shelves) {
        const v = p.values[sh.id];
        if (v == null || !Number.isFinite(v)) continue;
        sum += v;
        n += 1;
      }
      if (n > 0) perBucket.push(sum / n);
    }
    if (!perBucket.length) return null;

    const hist = new Array(20).fill(0) as number[];
    for (const v of perBucket) hist[Math.min(19, Math.max(0, Math.floor(v / 5)))] += bucketMin;

    return {
      avg: perBucket.reduce((a, b) => a + b, 0) / perBucket.length,
      bucketMin,
      perBucket,
      totalMinutes: perBucket.length * bucketMin,
      hist: hist.map((minutes, i) => ({ from: i * 5, minutes })),
    };
  }, [ts, granularity]);

  // Days actually spanned by the picked range (inclusive) — the denominator
  // for "N ч ниже X% = Y полных дней из Z выбранных" (Alexey 2026-09-06):
  // without a day count, a bare hour figure has nothing to compare against.
  const daysInPeriod = useMemo(() => {
    const a = new Date(`${from}T00:00:00Z`).getTime();
    const b = new Date(`${to}T00:00:00Z`).getTime();
    if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return null;
    return Math.round((b - a) / 86_400_000) + 1;
  }, [from, to]);

  if (!stats) return null;
  const showHours = granularity !== 'day';
  const minutesBelow = stats.perBucket.filter((v) => v < emptyPct).length * stats.bucketMin;

  const totalHours = stats.totalMinutes / 60;
  // "38ч ниже 50%" means nothing without a base to read it against (Alexey
  // 2026-09-06) — express it both in worked hours AND in "full days" (a day
  // being this period's own average working length, totalHours/daysInPeriod)
  // so a manager can read it as "лишился почти 2.5 рабочих дней", not just a
  // bare hour count.
  const dayEquivalent =
    daysInPeriod && stats.totalMinutes > 0 ? (minutesBelow * daysInPeriod) / stats.totalMinutes : null;

  // The average's label is centred on its 5%-bin; a corner label it would
  // overlap is dropped (the average itself is the more useful number there).
  const avgBin = Math.floor(stats.avg / 5) * 5;
  const avgLabel = `${Math.round(stats.avg)}%`;
  let hideZero: boolean;
  let hideHundred: boolean;
  if (bandW > 0) {
    const centre = (bandW * (avgBin + 2.5)) / 100;
    const half = (avgLabel.length * 6) / 2 + 4; // ~6px per glyph at 10px
    hideZero = centre - half < 16 + 2 * 6; // «0%» spans inset 16px + 2 glyphs
    hideHundred = centre + half > bandW - 16 - 4 * 6; // «100%»: 4 glyphs
  } else {
    hideZero = avgBin <= 10;
    hideHundred = avgBin >= 85;
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Card>
        <CardContent className="px-4 pt-3 pb-0">
          <div className="text-xs text-muted-foreground">Средняя заполненность за период</div>
          <div className="mt-1 text-4xl font-semibold leading-none tabular-nums">
            {Math.round(stats.avg)}%
          </div>
          {/* The distribution is the card's own footer band: bars flush, full-bleed
              and tinted (down to the card's own bottom edge — no padding below
              the bars — Alexey 2026-09-06: "низ должен начинаться прямо снизу
              карточки"), so it reads as texture under the number rather than a
              second chart. 0%/100% corner labels give the bars' horizontal scale
              without a real axis; the average's own value sits right above the
              dashed reference line so the line reads as "this is the average",
              not a stray mark — both readable WITHOUT hovering, hover just adds
              the per-bin breakdown (Alexey 2026-08-11 / 2026-09-06). */}
          <div ref={setBandEl} className="relative -mx-4 mt-3 h-[72px] overflow-hidden rounded-b-xl">
            <div className="pointer-events-none absolute inset-x-4 top-0 z-10 flex justify-between text-[10px] text-muted-foreground/70">
              <span className={hideZero ? 'invisible' : undefined}>0%</span>
              <span className={hideHundred ? 'invisible' : undefined}>100%</span>
            </div>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.hist} margin={{ top: 14, right: 0, bottom: 0, left: 0 }} barCategoryGap={0}>
                {/* Hidden, but REQUIRED: without it recharts falls back to the row
                    INDEX, so the tooltip read "13-18%" and topped out at "19-24%"
                    instead of walking 0->100 (Alexey 2026-08-11). All 20 bins are
                    always emitted, so the axis spans a hard 0-100% even when the
                    rack only ever occupies a few of them. */}
                <XAxis dataKey="from" type="category" hide />
                <Tooltip
                  isAnimationActive={false}
                  cursor={{ fill: 'var(--color-muted)', fillOpacity: 0.5 }}
                  contentStyle={{
                    fontSize: 12,
                    borderRadius: 8,
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-card)',
                    color: 'var(--color-foreground)',
                  }}
                  formatter={(v) => [
                    <>
                      <span className="font-semibold text-foreground">{fmtDuration(v as number)}</span>{' '}
                      <span className="text-muted-foreground">
                        (из {fmtDuration(stats.totalMinutes)} рабочих в выбранном периоде)
                      </span>
                    </>,
                    '',
                  ]}
                  labelFormatter={(l) => `Заполненность ${l}–${Number(l) + 5}%`}
                />
                <ReferenceLine
                  x={avgBin}
                  stroke="var(--color-foreground)"
                  strokeDasharray="3 2"
                  strokeOpacity={0.5}
                  label={{
                    value: avgLabel,
                    position: 'top',
                    fontSize: 10,
                    fill: 'var(--color-foreground)',
                    fillOpacity: 0.7,
                  }}
                />
                <Bar dataKey="minutes" fill="var(--color-primary)" fillOpacity={0.28} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="px-4 py-3">
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <span>Часов с заполненностью ниже</span>
            {/* Quiet stepper: muted until hovered, so the card still reads as a
                number and not as a form. */}
            <button
              type="button"
              aria-label="Уменьшить порог"
              disabled={emptyPct <= EMPTY_PCT_MIN}
              onClick={() => setEmptyPct((v) => Math.max(EMPTY_PCT_MIN, v - EMPTY_PCT_STEP))}
              className="rounded p-0.5 text-muted-foreground/50 transition-colors hover:text-foreground disabled:opacity-30"
            >
              <ChevronLeft className="size-3.5" />
            </button>
            <span className="font-semibold tabular-nums text-foreground">{emptyPct}%</span>
            <button
              type="button"
              aria-label="Увеличить порог"
              disabled={emptyPct >= EMPTY_PCT_MAX}
              onClick={() => setEmptyPct((v) => Math.min(EMPTY_PCT_MAX, v + EMPTY_PCT_STEP))}
              className="rounded p-0.5 text-muted-foreground/50 transition-colors hover:text-foreground disabled:opacity-30"
            >
              <ChevronRight className="size-3.5" />
            </button>
          </div>
          {showHours ? (
            <>
              <div className="mt-1 text-4xl font-semibold leading-none tabular-nums">
                {minutesBelow >= 60
                  ? (minutesBelow / 60).toFixed(minutesBelow / 60 < 10 ? 1 : 0)
                  : (minutesBelow / 60).toFixed(1)}
                <span className="ml-1 text-lg font-normal text-muted-foreground">ч</span>
              </div>
              {daysInPeriod != null && dayEquivalent != null && (
                <div className="mt-1 text-xs text-muted-foreground">
                  из {Math.round(totalHours)} ч рабочих, эквивалентно {dayEquivalent.toFixed(1)} полных
                  дня из {daysInPeriod} выбранных
                </div>
              )}
            </>
          ) : (
            <div className="mt-2 text-xs text-muted-foreground">
              На периоде больше 5 недель замеры усредняются по суткам, поэтому часы
              посчитать нельзя — сузьте период.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function RackStatsSection({
  zoneId,
  from,
  to,
  rack,
  controls,
  zoom,
  onZoom,
}: {
  zoneId: string;
  from: string;
  to: string;
  rack: LayoutDetailRack;
  /** Rendered in the section's top row (the rack picker lives INSIDE the
   *  panel — там же, где график, как везде). */
  controls?: React.ReactNode;
  /** Sub-day window of the picked period (?time=), null = whole days. The
   *  marquee sets it through onZoom — it IS the period, not a local zoom (#434). */
  zoom: [number, number] | null;
  onZoom: (w: [number, number] | null) => void;
}) {
  const ft = useFrameTime();
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  // The picked period in ms — the marquee's reset target and photo domain.
  const periodWindow = useMemo<[number, number]>(
    () => [new Date(`${from}T00:00:00`).getTime(), new Date(`${to}T23:59:59`).getTime()],
    [from, to],
  );
  // Keep INTRA-DAY resolution as long as possible: the anomalies live inside
  // the day, so a week must still read as daily curves at 15-min detail and
  // a month as hourly ones; 'day' only past ~5 weeks.
  const spanDays = Math.max(1, Math.round((periodWindow[1] - periodWindow[0]) / 86400000));
  const baseGran: '15min' | 'hour' | 'day' = spanDays <= 7 ? '15min' : spanDays <= 35 ? 'hour' : 'day';

  // NO JS height measurement. The old code measured the panel to derive the
  // photo height, but that measurement changed once its own result was applied
  // (measure → setState → re-render → re-measure), so the panel oscillated
  // up/down several times instead of settling once (Alexey 2026-07-21:
  // «пляшет … а не один раз»). The strip now uses a FIXED per-lane height, so
  // it's correct on the first paint and never re-measures; the chart is flex-1
  // and fills whatever height is left below it via pure CSS. The card is free
  // to be as tall as its content — the point is a SINGLE settle, no loop.
  // FIXED over-request (not derived from a measured width). photoCount used to
  // come from useFitCount, so as the container width settled on load it changed
  // → the SWR key changed → a DIFFERENT picked set was refetched → photos
  // flickered in/out (Alexey 2026-07-21). A constant keeps the fetch key stable;
  // the strip renders as many of these as fit, so the photos load once and stay.
  const photoCount = 40;

  // Zoomed → refetch the window at 15-min resolution (the base multi-day
  // granularity is too coarse to zoom into meaningfully).
  // Два запроса вместо одного: график приходит быстро и рисуется сразу, а
  // подбор 40 кадров идёт отдельно и закрывается скелетоном только фотополоса —
  // раньше всё ждало фотографий (Алексей 16.09, п. 55).
  const zoomOpts = zoom
    ? { fromTs: new Date(zoom[0]).toISOString(), toTs: new Date(zoom[1]).toISOString() }
    : {};
  const { data, isValidating } = useSWR(
    zoneId
      ? ['analytics:rack-stats', zoneId, rack.id, from, to, zoom?.[0] ?? 0, zoom?.[1] ?? 0]
      : null,
    () =>
      getLayoutDetail(zoneId, from, to, rack.id, zoom ? zoomGranularity(zoom) : baseGran, {
        photoCount: 0,
        ...zoomOpts,
      }).then((r) => r.data),
    { keepPreviousData: true, revalidateOnFocus: false },
  );
  const { data: photosData, isValidating: photosValidating } = useSWR(
    zoneId
      ? ['analytics:rack-photos', zoneId, rack.id, from, to, zoom?.[0] ?? 0, zoom?.[1] ?? 0, photoCount]
      : null,
    () =>
      getLayoutDetail(zoneId, from, to, rack.id, zoom ? zoomGranularity(zoom) : baseGran, {
        // Empty-shelf incidents first, then even spread — «Выкладка» leads with
        // the day's problems instead of a left-clustered flat spread (609).
        photoMode: 'problems',
        photoCount,
        ...zoomOpts,
      }).then((r) => r.data),
    { keepPreviousData: true, revalidateOnFocus: false },
  );

  // Frame the chart X-axis and photo-strip axis to the branch's WORKING HOURS
  // (padded, single-day) that the backend returns, instead of the raw
  // 00:00–23:59 calendar day (Alexey 2026-07-21: "рамка всё ещё с 00:00").
  // Multi-day or missing working_hours → fall back to the full picked period.
  // periodWindow stays the granularity/zoom-clamp basis; frameWindow is only the
  // display domain, so zoom-out still resets to the working window.
  const frameWindow = useMemo<[number, number]>(() => {
    const wh = data?.working_hours;
    if (wh?.start && wh?.end) {
      const d = whDomain(wh); // padded VISUAL domain (chart + strip); timing → wh.start (888)
      const s = new Date(d.start).getTime();
      const e = new Date(d.end).getTime();
      if (Number.isFinite(s) && Number.isFinite(e) && e > s) return [s, e];
    }
    return periodWindow;
  }, [data?.working_hours, periodWindow]);
  const win = zoom ?? frameWindow;

  const stripPhotos: DayStripPhoto[] = useMemo(
    () => buildRackStrip(photosData?.rack_photos ?? []),
    [photosData?.rack_photos],
  );
  // Lightbox gallery = EXACTLY the photos the strip renders (reported via
  // onVisiblePhotos), so swiping never pages past what's on screen.
  const [lbPhotos, setLbPhotos] = useState<DayStripPhoto[]>([]);
  const srcs = lbPhotos.map((p) => p.image_url!).filter(Boolean);
  // Multi-day ranges: captions carry the day («пн 14:20»), not a bare clock.
  const captionTime = (iso: string) =>
    spanDays > 1 ? fmtDayTime(new Date(iso).getTime()) : ft(iso);
  const captions = lbPhotos.filter((p) => p.image_url).map((p) => {
    const tlabel = captionTime(p.at);
    return `${rack.name}${p.metric ? ` · ${p.metric}` : ''}${tlabel ? ` · ${tlabel}` : ''}`;
  });

  // The chart draws 15min AND hour on a real time axis; without a pinned domain
  // the hour axis spanned dataMin..dataMax while the marquee mapped pixels over
  // `win` → a selection landed on different hours than the ones under the
  // cursor (Alexey 13.10 #5799). Pin both to `win`.
  const timeAxis = zoom != null || baseGran !== 'day';
  const [origAspect, setOrigAspect] = useState<boolean>(() => {
    try { return localStorage.getItem(ORIG_ASPECT_KEY) === '1'; } catch { return false; }
  });
  const toggleOrigAspect = () => setOrigAspect((v) => {
    try { localStorage.setItem(ORIG_ASPECT_KEY, v ? '0' : '1'); } catch { /* приватное окно */ }
    return !v;
  });
  // Полигон стеллажа (одна выборка на зону, кэш SWR) → пропорции на кадре.
  const { data: rackGeom } = useSWR(
    // Геометрия нужна и без режима пропорций: по удержанию в лайтбоксе — полный кадр с контуром (#2637).
    zoneId ? ['layout:rackGeom', zoneId] : null,
    async () => {
      const [racks, areas] = await Promise.all([listRacksByZone(zoneId), listAreasByZone(zoneId)]);
      const areaById = new Map(areas.data.results.map((a) => [String(a.id), a]));
      const out: Record<string, { geom: OrigGeom; aspect: number } | null> = {};
      for (const r of racks.data.results) {
        const geom = origGeomFromPolygon(areaById.get(String(r.area))?.direction_vectors);
        const fa = r.initial_width > 0 && r.initial_height > 0 ? r.initial_width / r.initial_height : FRAME_ASPECT;
        out[String(r.id)] = geom ? { geom, aspect: origBoxAspect(geom, fa) } : null;
      }
      return out;
    },
    { revalidateOnFocus: false },
  );
  const origEntry = origAspect ? (rackGeom?.[String(rack.id)] ?? null) : null;
  const origAspectValue = origEntry?.aspect ?? null;
  // Лайтбокс (Алексей 26.10 #2636/#2637): в «оригинальных пропорциях» — тот же прямоугольник кадра,
  // что в полосе, а не выровненный кроп; удержание — полный кадр с контуром стеллажа (как в
  // дашборде смены). Кадра нет — как раньше, сохранённый кроп.
  const lbGeom = rackGeom?.[String(rack.id)]?.geom ?? null;
  const lbShown = lbPhotos.filter((p) => p.image_url);
  const lbFrames = lbShown.map((p) => (lbGeom && p.frameUrl ? p.frameUrl : null));
  const lbPolys = lbShown.map((p) => (lbGeom && p.frameUrl ? [lbGeom.quad.map(([x, y]) => [x, y])] : null));
  const lbCrops = lbShown.map((p) => {
    if (!origEntry || !p.frameUrl) return null;
    const { x0, y0, x1, y1 } = origEntry.geom.bbox;
    return { frameSrc: p.frameUrl, polygons: [[[x0, y0], [x1, y0], [x1, y1], [x0, y1]]] };
  });

  return (
    <div className="space-y-3">
    {/* A NORMAL card that sits at the TOP of the tab (Alexey 2026-07-21 reversed
        the transparent/centered/fill treatment — the other stats tabs put their
        cards at the top, so this one should too). Content-sized with FIXED photo
        and chart heights, so nothing measures and nothing oscillates. */}
    <Card>
      <CardContent className="space-y-3 p-4">
        {controls && (
          // Полоска стеллажей — СЛЕВА и занимает всю оставшуюся ширину, крутилка
          // справа (Алексей 21.09, AI113). «Сброса» нет: выделение на графике
          // меняет сам период, назад — кнопкой «Назад» браузера (#434).
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-0 flex-1">{controls}</div>
            {/* keepPreviousData holds the previous rack's chart on screen during
                a switch, so there's no other loading cue at all otherwise
                (Alexey: "не реагирует лоадером на переключение... на всех
                страницах с этим графиком так же"). */}
            {(isValidating || photosValidating) && <Loader2 className="size-3.5 shrink-0 animate-spin text-muted-foreground" />}
            <UiTooltip content="Показывать фото в пропорциях стеллажа на кадре камеры"><button
              type="button"
              aria-pressed={origAspect}
              onClick={toggleOrigAspect}
              className={cn(
                'shrink-0 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                // Включено — спокойно: тонированный фон и цвет текста, не заливка (#1671 «слишком яркая»).
                origAspect
                  ? 'border-primary/40 bg-primary/10 text-primary'
                  : 'border-border bg-background text-muted-foreground hover:text-foreground',
              )}
            >
              Фото в оригинальных пропорциях
            </button></UiTooltip>
          </div>
        )}

        <div>
          {!photosData && (
            // Фото ещё подбираются — скелетон ровно на месте полосы, график ниже
            // при этом уже живой.
            <div className="animate-pulse rounded-xl bg-muted" style={{ height: RACK_STATS_IMG_H * 2 + 12 }} />
          )}
          {stripPhotos.length > 0 && (
            <DayPhotoStrip
              photos={stripPhotos}
              workStart={new Date(win[0]).toISOString()}
              workEnd={new Date(win[1]).toISOString()}
              onOpen={setOpenIdx}
              onVisiblePhotos={setLbPhotos}
              slotW={RACK_STATS_SLOT_W}
              imgH={RACK_STATS_IMG_H}
              lanes={2}
              // ≥6 even for wide crops — count wins over aspect (847/848).
              minCount={6}
              // Cover the whole day — even spread + surface midday dips — so the
              // opening stocking ramp doesn't crowd the afternoon out (zone 19).
              coverageFirst
              aspectOverride={origAspectValue}
              origGeom={origEntry?.geom ?? null}
              axisInset={{ left: 44, right: 8 }}
            />
          )}
        </div>

        {data ? (
          <MarqueeZoom
            domain={win}
            plotInset={{ left: 44, right: 8, bottom: 52 }}
            onZoom={(w) => onZoom([Math.max(w[0], periodWindow[0]), Math.min(w[1], periodWindow[1])])}
            onReset={() => onZoom(null)}
          >
            <ShelfTimeseriesChart
              data={data.shelf_timeseries}
              granularity={zoom ? zoomGranularity(zoom) : baseGran}
              label=""
              bare
              height={RACK_STATS_CHART_H}
              domain={timeAxis ? win : undefined}
            />
          </MarqueeZoom>
        ) : (
          <div className="animate-pulse rounded-xl bg-muted" style={{ height: RACK_STATS_CHART_H }} />
        )}
        {timeAxis && <QueueFlowStrip zoneId={zoneId} from={from} to={to} domain={win} />}
        {timeAxis && <RackSalesStrip zoneId={zoneId} rackId={rack.id} domain={win} />}

        {openIdx != null && (
          <ImageLightbox
            srcs={srcs}
            index={openIdx}
            alt=""
            captions={captions}
            frameSrcs={lbFrames}
            polygonsList={lbPolys}
            cropSpecs={lbCrops}
            primary="crop"
            holdHint="Удерживайте — полный кадр"
            onClose={() => setOpenIdx(null)}
            onNavigate={setOpenIdx}
          />
        )}
      </CardContent>
    </Card>
    {data ? (
      <RackPeriodSummary
        ts={data.shelf_timeseries}
        granularity={zoom ? zoomGranularity(zoom) : baseGran}
        from={from}
        to={to}
      />
    ) : (
      // Карточки сводки тоже получают скелетон, а не пустоту под графиком
      // (Алексей 16.09, п. 56–57: «скелетоны новых карточек не грузятся»).
      <SummarySkeleton />
    )}
    </div>
  );
}

function SummarySkeleton() {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="h-28 animate-pulse rounded-xl bg-muted" />
      <div className="h-28 animate-pulse rounded-xl bg-muted" />
    </div>
  );
}

// Узкая полоса сверху: по каждому стеллажу за период — средний процент
// выкладки и часы ниже нормы, как на графике (Алексей 16.09, п. 58). Клик по
// стеллажу открывает его график.
function RackMetricsStrip({
  zoneId,
  from,
  to,
  selectedId,
  onSelect,
  allSelected = false,
  onSelectAll,
}: {
  zoneId: string;
  from: string;
  to: string;
  selectedId: number | null;
  onSelect: (id: number) => void;
  /** «Все вместе» выбрано — чип подсвечен. */
  allSelected?: boolean;
  onSelectAll?: () => void;
}) {
  const { data } = useSWR(
    zoneId ? ['analytics:racks-summary', zoneId, from, to] : null,
    () => getLayoutRacksSummary(zoneId, from, to, EMPTY_PCT_DEFAULT).then((r) => r.data),
    { keepPreviousData: true, revalidateOnFocus: false },
  );
  if (!data) {
    return <div className="h-9 animate-pulse rounded-full bg-muted" />;
  }
  if (!data.racks.length) return null;
  const overall = overallFullness(data.racks);
  // Календарных дней в выбранном периоде — запасной делитель, если BE не сказал, сколько дней посчитано.
  const periodDays = Math.max(1, Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000) + 1);
  return (
    <div className="space-y-1.5">
    {/* Чипы — и есть переключатель стеллажей: селектор справа убран, «Все
        вместе» стоит первым чипом и несёт общую наполненность (Алексей 17.09,
        AI40); отдельная строка «Сопутствующая продукция: 75%» убрана (AI41). */}
    <div className="flex gap-2 overflow-x-auto pb-0.5">
      {onSelectAll && (
        <button
          type="button"
          onClick={onSelectAll}
          className={cn(
            'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition',
            allSelected ? 'border-primary/50 bg-primary/10 text-foreground' : 'border-border bg-card text-muted-foreground hover:text-foreground',
          )}
        >
          <span className="font-medium text-foreground">Все вместе</span>
          {overall && (
            <>
              {/* Только взвешенный процент, без вердикта: «76 % рядом с „заметная
                  проблема“ выглядит странно» (Алексей 12.10, #1467). Вердикты — у
                  стеллажей, часами ниже нормы. Формула — в (i). */}
              <span className="tabular-nums font-semibold text-foreground">{Math.round(overall.pct)}%</span>
              <InfoHint text={`Взвешенный средний процент выкладки по стеллажам за период: готовая еда ×2, десерты ×1.5, выпечка ×1, остальное не входит. Процент стеллажа — средняя по его полкам за все 15-минутные отрезки рабочего времени.${data.partial ? ' Посчитаны не все дни периода.' : ''}`} />
            </>
          )}
        </button>
      )}
      {data.racks.map((r) => {
        const active = r.id === selectedId;
        const below = r.minutes_below;
        // Делим на посчитанные открытые дни (выходные не размывают среднее), иначе — на календарные.
        const openDays = r.below_rule === 'day_report' && r.days ? r.days : periodDays;
        return (
          <UiTooltip key={r.id} content={below != null
              ? (r.below_rule === 'day_report'
                ? `${BELOW_RULE_HINT} За период: ${fmtDuration(below)}${r.status === 'red' ? ' · нехватка ≥ 40 % — заметная проблема' : r.status === 'yellow' ? ' · небольшая проблема' : ''}.`
                : r.below_rule === 'shelf_thresholds'
                  ? `Хотя бы одна полка ниже своего порога (${(r.shelf_thresholds || []).join('/')}%) суммарно ${fmtDuration(below)}`
                  : `Ниже ${data.below_pct}% суммарно ${fmtDuration(below)}`)
              : BELOW_RULE_HINT}><button
            type="button"
            onClick={() => onSelect(r.id)}
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition',
              active ? 'border-primary/50 bg-primary/10 text-foreground' : 'border-border bg-card text-muted-foreground hover:text-foreground',
            )}
          >
            <span className="font-medium text-foreground">{r.name}</span>
            <span className="tabular-nums">{r.avg_pct != null ? `${Math.round(r.avg_pct)}%` : '—'}</span>
            {below != null && below > 0 && (
              // Цвет часов — тот же, что у категории в отчёте дня и «Статусе» (#1453).
              <span className={cn('tabular-nums', STATUS_TEXT[r.status ?? 'yellow'])}>{fmtBelow(below, periodDays, openDays)}</span>
            )}
            {below != null && below === 0 && r.below_rule === 'day_report' && (
              <span className="text-emerald-600 dark:text-emerald-400">в норме</span>
            )}
          </button></UiTooltip>
        );
      })}
    </div>
    </div>
  );
}

/** Единое правило «ниже нормы» — то же, что в отчёте дня и «Статусе» (Алексей 12.10, #1453). */
const BELOW_RULE_HINT = 'Часы ниже нормы считаются как в отчёте дня: полка ниже своего порога на 20 п.п. и дольше 30 минут, среднее по полкам стеллажа; вечерняя распродажа после 16:00 (выпечка) и 19:00 (остальное) не считается; меньше 3 часов за день не показывается.';
const STATUS_TEXT: Record<'green' | 'yellow' | 'red', string> = {
  green: 'text-emerald-600 dark:text-emerald-400', yellow: 'text-amber-600 dark:text-amber-400', red: 'text-rose-600 dark:text-rose-400',
};

/** Веса типов в общей наполненности (согласованы с клиентом через Алексея, 11.09). */
const OVERALL_WEIGHTS: Record<string, number> = { 'Готовая еда': 2, 'Десерты': 1.5, 'Выпечка': 1 };
const OVERALL_FULLNESS_THRESHOLD = 70;

function overallFullness(racks: LayoutRackSummary[]): { pct: number; others: { type: string; pct: number }[] } | null {
  let sum = 0; let w = 0;
  const others = new Map<string, { sum: number; n: number }>();
  for (const r of racks) {
    if (r.avg_pct == null) continue;
    const weight = OVERALL_WEIGHTS[r.rack_type ?? ''];
    if (weight) { sum += r.avg_pct * weight; w += weight; continue; }
    const type = r.rack_type || 'Прочее';
    const o = others.get(type) ?? { sum: 0, n: 0 };
    o.sum += r.avg_pct; o.n += 1; others.set(type, o);
  }
  if (w === 0) return null;
  return {
    pct: sum / w,
    others: [...others.entries()].map(([type, o]) => ({ type, pct: o.sum / o.n })),
  };
}

// One compact per-rack chart for the «Все» grid (charts only — the point is
// comparing the racks' dynamics at a glance, like the report's «Все стеллажи»
// grid). Clicking a cell selects that rack, as if picked in the dropdown.
// Reimplemented locally (not RackMiniGrid) to avoid a circular import:
// RackMiniGrid already imports ShelfTimeseriesChart FROM this file.
function RackGridCell({
  zoneId,
  from,
  to,
  rack,
  granularity,
  onOpen,
}: {
  zoneId: string;
  from: string;
  to: string;
  rack: LayoutDetailRack;
  granularity: '15min' | 'hour' | 'day';
  onOpen: () => void;
}) {
  const { data } = useSWR(
    zoneId ? ['analytics:rack-grid', zoneId, rack.id, from, to] : null,
    () =>
      getLayoutDetail(zoneId, from, to, rack.id, granularity, {
        photoMode: 'even',
        photoCount: 1,
      }).then((r) => r.data),
    { keepPreviousData: true, revalidateOnFocus: false },
  );
  const timeAxis = granularity === '15min' || granularity === 'hour';
  const domain = useMemo<[number, number] | undefined>(() => {
    const wh = data?.working_hours;
    if (wh?.start && wh?.end) {
      const d = whDomain(wh); // padded VISUAL domain (888)
      const s = new Date(d.start).getTime();
      const e = new Date(d.end).getTime();
      if (Number.isFinite(s) && Number.isFinite(e) && e > s) return [s, e];
    }
    return undefined;
  }, [data?.working_hours]);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className="group relative w-full cursor-pointer rounded-xl border border-border bg-card p-2 text-left transition hover:border-primary/40 hover:shadow-sm"
    >
      <Maximize2 className="absolute right-1.5 top-1.5 z-10 size-3.5 text-muted-foreground/40 transition-colors group-hover:text-primary" />
      <div className="mb-1 truncate px-1 text-xs font-medium text-foreground">{rack.name}</div>
      {data ? (
        <ShelfTimeseriesChart
          data={data.shelf_timeseries}
          granularity={granularity}
          label=""
          bare
          height={180}
          domain={timeAxis ? domain : undefined}
          showLegend={false}
        />
      ) : (
        <div className="h-[180px] animate-pulse rounded-lg bg-muted" />
      )}
    </div>
  );
}

function TabLoading() {
  // Скелетон повторяет блоки страницы (полоса метрик, выбор стеллажа, фото,
  // график, две карточки сводки), а не один серый квадрат (Алексей 16.09, п. 56).
  return (
    <div className="space-y-3">
      <div className="h-9 animate-pulse rounded-full bg-muted" />
      <Card>
        <CardContent className="space-y-2 p-4">
          <div className="flex justify-end"><div className="h-8 w-44 animate-pulse rounded-full bg-muted" /></div>
          <div className="animate-pulse rounded-xl bg-muted" style={{ height: RACK_STATS_IMG_H * 2 + 12 }} />
          <div className="animate-pulse rounded-xl bg-muted" style={{ height: RACK_STATS_CHART_H }} />
        </CardContent>
      </Card>
      <SummarySkeleton />
    </div>
  );
}

function NoRacks({ message }: { message: string }) {
  return (
    <Card>
      <CardContent className="flex h-36 items-center justify-center text-sm text-muted-foreground">
        {message}
      </CardContent>
    </Card>
  );
}

// One rack at a time, full-width (a rack picker instead of the all-racks
// scroll — that read as a mess). The section zooms with the marquee and
// resets back to the picked period.
export default function AnalyticsLayoutDetailTab({
  zoneId,
  from,
  to,
  rackId,
  onRackChange,
  timeWindow,
  onTimeWindow,
}: {
  zoneId: string;
  from: string;
  to: string;
  /** ?rack=<id> (or 'all' → ALL_RACKS). Lives in the URL — an empty-shelf alert
   *  deep-link opens straight onto its rack (Alexey 2026-08-24), and the
   *  browser's Back steps through rack switches (#434). A foreign/unknown id
   *  safely falls back to the first rack below. */
  rackId: number | null;
  onRackChange: (id: number) => void;
  /** ?time= sub-day window of the period, picked by dragging over the chart. */
  timeWindow: [number, number] | null;
  onTimeWindow: (w: [number, number] | null) => void;
}) {
  const { t } = useT();
  const setRackId = onRackChange;

  // One cheap request for the rack list; the section fetches its own slice.
  const { data, error, isLoading } = useSWR(
    zoneId ? ['analytics:layout-racks', zoneId, from] : null,
    () =>
      getLayoutDetail(zoneId, from, from, undefined, 'day', {
        photoMode: 'even',
        photoCount: 1,
      }).then((r) => r.data),
    { revalidateOnFocus: false },
  );

  if (isLoading && !data) return <TabLoading />;
  if (error && !data) {
    return (
      <Card>
        <CardContent className="p-0">
          <ServerStatusNotice variant="card" error={error} />
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  if (!data.racks.length) {
    return <NoRacks message={t('analytics.layout.notConfigured')} />;
  }

  const selectedId = rackId ?? data.selected_rack?.id ?? data.racks[0].id;
  const selected = data.racks.find((r) => r.id === selectedId) ?? data.racks[0];
  const showAll = rackId === ALL_RACKS;
  const ALL_RACKS_VALUE = String(ALL_RACKS);

  // Grid granularity for «Все» — same span→resolution ladder as a single
  // RackStatsSection, so the mini charts read as daily curves for a week and
  // hourly for a month.
  const gridSpanDays = Math.max(
    1,
    Math.round(
      (new Date(`${to}T23:59:59`).getTime() - new Date(`${from}T00:00:00`).getTime()) / 86400000,
    ),
  );
  const gridGran: '15min' | 'hour' | 'day' =
    gridSpanDays <= 7 ? '15min' : gridSpanDays <= 35 ? 'hour' : 'day';

  // «Все» is a special FIRST item in the rack dropdown (mirrors «Все филиалы» in
  // the branch selector), not a separate toggle button (Alexey 2026-07-21).
  const pickerControls = (
    <div className="flex min-w-0 flex-1 items-center gap-2 sm:flex-none">
      <div className="min-w-0 flex-1 sm:min-w-44 sm:flex-none">
        <Combobox
          value={showAll ? ALL_RACKS_VALUE : String(selected.id)}
          onChange={(v) => setRackId(v === ALL_RACKS_VALUE ? ALL_RACKS : Number(v))}
          options={[
            { value: ALL_RACKS_VALUE, label: t('report.day.allRacks') },
            ...data.racks.map((r) => ({ value: String(r.id), label: r.name })),
          ]}
          className="h-8 rounded-full text-xs shadow-none"
          id="stats-rack-picker"
        />
      </div>
    </div>
  );

  // «Все» = a GRID of compact per-rack charts (like the report's «Все стеллажи»),
  // scrolling INSIDE the tab so the page chrome stays put.
  if (showAll) {
    return (
      <Card className="flex h-full min-h-0 flex-col">
        <CardContent className="flex min-h-0 flex-1 flex-col gap-3 p-4">
        <RackMetricsStrip
          zoneId={zoneId} from={from} to={to}
          selectedId={null} onSelect={setRackId}
          allSelected onSelectAll={() => setRackId(ALL_RACKS)}
        />
        {/* content-start: rows pack to the top at their natural (chart-sized)
            height instead of stretching to tile the screen — no empty space
            inside each cell; any leftover is OUTSIDE, below the grid (Alexey
            2026-07-21: «убрать пустое место снаружи, а не внутри»). */}
        <div className="grid min-h-0 flex-1 content-start grid-cols-1 gap-3 overflow-y-auto pr-0.5 sm:grid-cols-2 xl:grid-cols-3">
          {data.racks.map((r) => (
            <RackGridCell
              key={r.id}
              zoneId={zoneId}
              from={from}
              to={to}
              rack={r}
              granularity={gridGran}
              onOpen={() => setRackId(r.id)}
            />
          ))}
        </div>
        </CardContent>
      </Card>
    );
  }

  // Single rack = a NORMAL card at the TOP of the tab (like the other stats
  // tabs), not stretched to fill the height.
  return (
    <div className="h-full min-h-0 space-y-3 overflow-y-auto pr-0.5">
      <RackStatsSection
        zoneId={zoneId}
        from={from}
        to={to}
        rack={selected}
        zoom={timeWindow}
        onZoom={onTimeWindow}
        controls={
          <RackMetricsStrip
            zoneId={zoneId} from={from} to={to}
            selectedId={selected.id} onSelect={setRackId}
            onSelectAll={() => setRackId(ALL_RACKS)}
          />
        }
      />
    </div>
  );
}
